import React from 'react';
import { useTranslation } from 'react-i18next';
import { createInstagramChannelAccount } from '../services/channelAccountService';
import { ToggleField } from '../../../shared/components';
import styles from './ConnectorsPage.module.css';

interface AddInstagramAccountFormProps {
  canManage: boolean;
  onCreated: () => void;
  onCancel: () => void;
}

const emptyForm = {
  displayName: '',
  instagramBusinessAccountId: '',
  pageAccessToken: '',
  apiBaseUrl: 'https://graph.instagram.com',
  apiVersion: 'v25.0',
  instagramUsername: '',
  defaultCommentReplyMode: 'public_comment',
  isDefault: false,
};

export const AddInstagramAccountForm: React.FC<AddInstagramAccountFormProps> = ({
  canManage,
  onCreated,
  onCancel,
}) => {
  const { t } = useTranslation(['companies', 'admin']);
  const [form, setForm] = React.useState(emptyForm);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const save = async () => {
    if (!form.displayName.trim() || !form.instagramBusinessAccountId.trim() || !form.pageAccessToken.trim()) {
      setError(t('channelAccountAddRequiredFields', { ns: 'companies' }));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await createInstagramChannelAccount({
        displayName: form.displayName.trim(),
        instagramBusinessAccountId: form.instagramBusinessAccountId.trim(),
        pageAccessToken: form.pageAccessToken.trim(),
        apiBaseUrl: form.apiBaseUrl.trim() || undefined,
        apiVersion: form.apiVersion.trim() || undefined,
        instagramUsername: form.instagramUsername.trim() || undefined,
        defaultCommentReplyMode: form.defaultCommentReplyMode,
        isDefault: form.isDefault,
      });
      setForm(emptyForm);
      onCreated();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : t('failedToCreateChannelAccount', { ns: 'companies' }));
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) {
    return null;
  }

  return (
    <div className={styles.channelAccountAddForm}>
      <h4>{t('channelAccountAddInstagramHeading', { ns: 'companies' })}</h4>
      <p style={{ color: '#516173', marginTop: 0 }}>
        {t('channelAccountAddInstagramHelp', { ns: 'companies' })}
      </p>

      {error ? <div className="error">{error}</div> : null}

      <div className="company-form-grid">
        <label>
          {t('channelAccountDisplayName', { ns: 'companies' })}
          <input
            className="text-input"
            value={form.displayName}
            onChange={event => setForm(prev => ({ ...prev, displayName: event.target.value }))}
            placeholder={t('channelAccountDisplayNamePlaceholder', { ns: 'companies' })}
          />
        </label>

        <label>
          {t('instagramBusinessAccountId', { ns: 'companies' })}
          <input
            className="text-input"
            value={form.instagramBusinessAccountId}
            onChange={event => setForm(prev => ({ ...prev, instagramBusinessAccountId: event.target.value }))}
          />
        </label>

        <label>
          {t('instagramAccessToken', { ns: 'companies' })}
          <input
            className="text-input"
            type="password"
            value={form.pageAccessToken}
            onChange={event => setForm(prev => ({ ...prev, pageAccessToken: event.target.value }))}
            autoComplete="new-password"
          />
        </label>

        <ToggleField
          label={t('channelAccountDefaultLabel', { ns: 'companies' })}
          checked={form.isDefault}
          onChange={value => setForm(prev => ({ ...prev, isDefault: value }))}
        />
      </div>

      <div className="company-actions-inline" style={{ marginTop: 12 }}>
        <button type="button" className="btn btn-primary" onClick={() => { void save(); }} disabled={saving}>
          {saving ? t('savingEllipsis', { ns: 'admin' }) : t('channelAccountCreate', { ns: 'companies' })}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>
          {t('channelAccountCancelAdd', { ns: 'companies' })}
        </button>
      </div>
    </div>
  );
};
